import { useTranslation } from 'next-i18next'

import { DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS } from '@src/components/blocks/edd-message-container-ds'
import {
  checkHolidayMessageShown,
  hasValidDisplayTags,
} from '@src/components/blocks/product-cta-ds/holiday-message.utils'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { useStatsigDynamicHolidayConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-holiday-config.hook'
import { getBaseMessagingConditions } from '@src/quince-components/src/atoms/productCTA/productCTA'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import type { ProductCTAType } from '@src/quince-components/src/atoms/productCTA/types'
import { useGoldBarProductEligibility } from '@src/quince-components/src/atoms/productCTA/useGoldBarProductEligibility'
import { getIsGiftCard } from '@src/quince-components/src/atoms/productCTA/utils'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, { CUSTOM_FIELD_NAMES } from '@src/quince-site-common/src/helpers/productHelper'
import useEddExp from '@src/quince-site-common/src/hooks/edd/hooks/useEddExp'
import { getCutoffTime } from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'

import type { ProductButtonBagDSVariant } from './product-button-bag-ds'

/**
 * Derives the Figma ProductButtonBag `variant` (node 8719:17323) from real purchase state.
 * Reuses the exact gates the composed components apply — `useProductCTAType`'s resolved `type`
 * (threaded in as `props.type`, never recomputed), `getBaseMessagingConditions`, the holiday
 * config match, the EDD-region gate from `ProductCTADSWrapper`, and `EDDMessageContainerDS`'s
 * branch order — so the passed variant always agrees with what the state-owning children render.
 *
 * Called by `ProductCTADSWrapper`; call sites that render `ProductButtonBagDS` directly must
 * compute the variant with this hook (the prop is required by design — no internal fallback).
 */
export const useProductButtonBagDSVariant = (props: ProductCTAType): ProductButtonBagDSVariant => {
  const type = props.type as CTA_TYPE
  const { selectedVariantPurchaseInfo, product } = props
  const eddDetail = selectedVariantPurchaseInfo?.eddDetail

  const { locale } = useLocaleContext()
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const { isEddExpEnabled: eddExpEnabled, isInitialized: eddExpInit } = useEddExp()
  const disableForceEDD = useStatsigDynamicConfig<boolean>(DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS)
  const { isGoldBarError } = useGoldBarProductEligibility(product)
  const { zipCode } = useZipCode()
  const { t } = useTranslation('common', { keyPrefix: 'edd' })

  const isAgeRestricted = product ? productHelper.getIsAgeRestrictedProduct(product) : false
  // Force-EDD for age-restricted products — same rule as ProductCTADS / EDDMessageContainerDS.
  const isEDDExpEnabled = eddExpEnabled || (isAgeRestricted && !disableForceEDD)

  // Type-resolved variants first — identical precedence to the CTA switch.
  if (type === CTA_TYPE.PREORDER) {
    return 'preorder'
  }
  if (type === CTA_TYPE.WAITLIST) {
    if (selectedVariantPurchaseInfo?.hasJoinedWaitList) {
      return 'waitlisted'
    }
    // Same client-side login check `ProductWaitlistCTADS` uses to decide the email input.
    return AuthUtil.isLoggedIn() ? 'outOfStock' : 'outOfStockGuest'
  }
  if (type === CTA_TYPE.OUT_OF_STOCK) {
    return 'outOfStock'
  }

  // ADD_TO_CART / SHOP_IN_THE_APP — the message family.
  const isNonPhysicalProduct = product
    ? productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_NON_PHYSICAL_PRODUCT)
    : false
  if (isNonPhysicalProduct) {
    return 'email'
  }

  const baseMessagingConditions = getBaseMessagingConditions(
    selectedVariantPurchaseInfo,
    props.showVariantAvailableInventory,
    product,
    props.areAllVariantOOS ?? false,
    isGoldBarError,
    eddExpInit,
  )

  const displayTags = eddDetail?.displayTags
  const holidayMessageShown =
    baseMessagingConditions &&
    hasValidDisplayTags(displayTags, holidayConfig) &&
    !!displayTags &&
    checkHolidayMessageShown(displayTags, holidayConfig, locale)

  if (holidayMessageShown && !props.isPostOrderFlow) {
    return 'eddChristmas'
  }

  // EDD-region gate — `ProductCTADSWrapper`'s `showEDDMsg` plus the container's own null-guard.
  const showEDDMsg = productHelper.getIsAgeRestrictedProduct(product as Product)
    ? type === CTA_TYPE.ADD_TO_CART
    : !(
        Boolean(selectedVariantPurchaseInfo?.isPreorder) ||
        Boolean(selectedVariantPurchaseInfo?.showMadeToOrder) ||
        getIsGiftCard(product?.id) ||
        Boolean(product?.isMadeToOrder)
      )
  const eddRegionActive =
    showEDDMsg &&
    !!product &&
    !isGoldBarError &&
    !props.isPostOrderFlow &&
    isEDDExpEnabled &&
    eddDetail?.showEDD !== undefined

  if (eddRegionActive) {
    // Branch order mirrors EDDMessageContainerDS exactly.
    if (!zipCode) {
      return 'stockEnterZip'
    }
    if (!eddDetail.isServiceable) {
      return 'stockUpdateZip'
    }
    const isCutoffExpired = !!eddDetail.cutoffDate && !getCutoffTime(eddDetail.cutoffDate, t)
    if (!eddDetail.showEDD || isCutoffExpired) {
      return 'stockValidZip'
    }
    // Express / premium-2-day / default EDD rows are all the Figma `edd` variant.
    return 'edd'
  }

  const showInStockMessage = props.isPostOrderFlow
    ? true
    : baseMessagingConditions && !isEDDExpEnabled && !holidayMessageShown
  if (showInStockMessage) {
    return 'stock'
  }

  return 'buttonOnly'
}

export default useProductButtonBagDSVariant
